import { Commit } from 'vuex';

import { memberService } from '@/bundles/Members/factory/memberServiceFactory';
import Member from '@/bundles/Members/models/Member';
import { INameValue } from '@/types/common';
import { Nullable } from '@/utils/types';

import * as constants from './members-types';

export interface IActiveMembersState {
  activeMembers: Nullable<Member[]>;
  activeMembersRequest: Nullable<Promise<Member[]>>;
}

// Every caller of the cache sends this exact query, which is what makes one response shareable.
const activeMembersParams: INameValue<number | string>[] = [
  { name: 'take', value: 0 }, // zero returns every member
  { name: 'status', value: 'active' },
];

export const getActiveMembersState = (): IActiveMembersState => ({
  activeMembers: null,
  activeMembersRequest: null,
});

export const activeMembersMutations = {
  [constants.SET_ACTIVE_MEMBERS] (state: IActiveMembersState, members: Member[]) {
    state.activeMembers = members;
    state.activeMembersRequest = null;
  },
  [constants.SET_ACTIVE_MEMBERS_REQUEST] (state: IActiveMembersState, request: Nullable<Promise<Member[]>>) {
    state.activeMembersRequest = request;
  },
  [constants.RESET_ACTIVE_MEMBERS] (state: IActiveMembersState) {
    state.activeMembers = null;
    state.activeMembersRequest = null;
  },
};

export const activeMembersActions = {
  // The dashboard widgets all ask for the same unbounded member list while the page loads.
  // Serve them one response: cached once it lands, shared through the in-flight promise until then.
  // Callers get their own array so their local sorting cannot reach into the store.
  [constants.LOAD_ACTIVE_MEMBERS] (
    { state, commit }: { state: IActiveMembersState; commit: Commit }
  ): Promise<Member[]> {
    if (state.activeMembers) {
      return Promise.resolve([...state.activeMembers]);
    }

    if (state.activeMembersRequest) {
      return state.activeMembersRequest.then((members) => [...members]);
    }

    const request = memberService.list(activeMembersParams)
      .then(({ data }) => {
        commit(constants.SET_ACTIVE_MEMBERS, data);

        return data;
      })
      .catch((error) => {
        commit(constants.RESET_ACTIVE_MEMBERS);

        throw error;
      });

    commit(constants.SET_ACTIVE_MEMBERS_REQUEST, request);

    return request.then((members) => [...members]);
  },
};
